import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

type Theme = "dark" | "light";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  /** user-selected theme (dark | light) — app is currently forced to light */
  theme: Theme;
  /** actual theme applied to the document (dark | light) */
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeProviderState | undefined>(undefined);

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "smartcare-theme",
}: ThemeProviderProps) {
  // 1) read initial preference once (safe on client)
  // Start explicitly in the default theme (we're intentionally not honoring a
  // previously stored dark/system preference for the "just for now" request).
  const [theme, setThemeState] = useState<Theme>(() => defaultTheme);

  // 2) resolved theme is simply the selected theme (no system auto-detection)
  const resolvedTheme: "light" | "dark" = theme;

  // 3) apply classes/attributes on documentElement
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(resolvedTheme);
    root.setAttribute("data-theme", resolvedTheme); // useful for CSS variable theming
  }, [resolvedTheme]);

  // 4) no system preference syncing – app uses explicit user-selected theme only

  // 5) public setter (persists + updates state)
  // Public setter is intentionally constrained to keep the app in light mode.
  // Any attempts to switch to dark will be ignored so the UI remains light-only.
  const setTheme = (_next: Theme) => {
    try {
      localStorage.setItem(storageKey, "light");
    } catch {}
    setThemeState("light");
  };

  const value: ThemeProviderState = { theme, resolvedTheme, setTheme };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
